const API_BASE = import.meta.env.VITE_API_URL || '';

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

class ApiClient {
  private getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {} } = options;
    const token = this.getToken();

    const config: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
    };

    if (body) {
      config.body = JSON.stringify(body);
    }

    const response = await fetch(`${API_BASE}/api${endpoint}`, config);

    if (response.status === 401) {
      // Try to refresh token
      const refreshed = await this.refreshToken();
      if (refreshed) {
        // Retry the request with new token
        const newToken = this.getToken();
        config.headers = {
          ...config.headers as Record<string, string>,
          Authorization: `Bearer ${newToken}`,
        };
        const retryResponse = await fetch(`${API_BASE}/api${endpoint}`, config);
        if (!retryResponse.ok) {
          const error = await retryResponse.json().catch(() => ({ message: 'Request failed' }));
          throw new Error(error.message || `HTTP ${retryResponse.status}`);
        }
        return retryResponse.json();
      }
      // Clear tokens and redirect to login
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
      throw new Error('Session expired');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  private async refreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) return false;

      const data = await response.json();
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      return true;
    } catch {
      return false;
    }
  }

  // Auth
  async register(data: { email: string; password: string; first_name: string; last_name: string; role: string; invite_code?: string }) {
    return this.request<any>('/auth/register', { method: 'POST', body: data });
  }

  async login(data: { email: string; password: string }) {
    return this.request<any>('/auth/login', { method: 'POST', body: data });
  }

  async logout() {
    return this.request<any>('/auth/logout', { method: 'POST' });
  }

  async getMe() {
    return this.request<any>('/auth/me');
  }

  // User profiles
  async updateStudentProfile(data: any) {
    return this.request<any>('/users/student', { method: 'PUT', body: data });
  }

  async updateResearcherProfile(data: any) {
    return this.request<any>('/users/researcher', { method: 'PUT', body: data });
  }

  async updateUser(data: any) {
    return this.request<any>('/users/me', { method: 'PUT', body: data });
  }

  // Studies
  async getAvailableStudies(params: { page?: number; per_page?: number; search?: string; category?: string } = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.per_page) query.set('per_page', String(params.per_page));
    if (params.search) query.set('search', params.search);
    if (params.category) query.set('category', params.category);
    return this.request<any>(`/studies/available?${query}`);
  }

  async getStudy(id: string) {
    return this.request<any>(`/studies/${id}`);
  }

  async getMyStudies(params: { page?: number; per_page?: number } = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.per_page) query.set('per_page', String(params.per_page));
    return this.request<any>(`/studies/mine?${query}`);
  }

  async createStudy(data: any) {
    return this.request<any>('/studies', { method: 'POST', body: data });
  }

  async updateStudy(id: string, data: any) {
    return this.request<any>(`/studies/${id}`, { method: 'PUT', body: data });
  }

  async activateStudy(id: string) {
    return this.request<any>(`/studies/${id}/activate`, { method: 'POST' });
  }

  async deleteStudy(id: string) {
    return this.request<any>(`/studies/${id}`, { method: 'DELETE' });
  }

  // Slots
  async createSlots(studyId: string, data: { slots: any[] }) {
    return this.request<any>(`/studies/${studyId}/slots`, { method: 'POST', body: data });
  }

  async getSlots(studyId: string) {
    return this.request<any>(`/studies/${studyId}/slots`);
  }

  // Appointments
  async bookAppointment(data: { study_id: string; slot_id: string }) {
    return this.request<any>('/appointments/book', { method: 'POST', body: data });
  }

  async getMyAppointments(params: { page?: number; status?: string } = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.status) query.set('status', params.status);
    return this.request<any>(`/appointments/mine?${query}`);
  }

  async cancelAppointment(id: string) {
    return this.request<any>(`/appointments/${id}`, { method: 'DELETE' });
  }

  async getStudyAppointments(studyId: string) {
    return this.request<any>(`/appointments/study/${studyId}`);
  }

  async completeAppointment(id: string) {
    return this.request<any>(`/appointments/${id}/complete`, { method: 'PUT' });
  }

  async markNoShow(id: string) {
    return this.request<any>(`/appointments/${id}/no-show`, { method: 'PUT' });
  }

  // Stats
  async getStudentStats() {
    return this.request<any>('/stats/student');
  }

  async getResearcherStats() {
    return this.request<any>('/stats/researcher');
  }

  // Notifications
  async getNotifications(params: { page?: number; unread?: boolean } = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.unread) query.set('unread', 'true');
    return this.request<any>(`/notifications?${query}`);
  }

  async getUnreadCount() {
    return this.request<{ count: number }>('/notifications/unread-count');
  }

  async markNotificationRead(id: string) {
    return this.request<any>(`/notifications/${id}/read`, { method: 'PUT' });
  }

  async markAllRead() {
    return this.request<any>('/notifications/read-all', { method: 'PUT' });
  }

  // Ambassador
  async createInviteCode(data: { max_uses?: number; expires_at?: string }) {
    return this.request<any>('/ambassadors/codes', { method: 'POST', body: data });
  }

  async getInviteCodes(params: { page?: number } = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    return this.request<any>(`/ambassadors/codes?${query}`);
  }

  async getAmbassadorStats() {
    return this.request<any>('/ambassadors/stats');
  }

  async getReferrals(params: { page?: number } = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    return this.request<any>(`/ambassadors/referrals?${query}`);
  }

  // AI
  async generateDescription(data: { title: string; notes: string; category?: string }) {
    return this.request<{ description: string; ai_unavailable?: boolean }>('/ai/generate-description', { method: 'POST', body: data });
  }

  async suggestCriteria(data: { title: string; description: string }) {
    return this.request<{ criteria: any; ai_unavailable?: boolean }>('/ai/suggest-criteria', { method: 'POST', body: data });
  }
}

export const api = new ApiClient();
